import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ChevronDown, Lock, Menu, X } from "lucide-react";
import { useLang } from "@/i18n";
import { NAV } from "@/content/nav";
import { LogoFull } from "./Logo";

export const Header = () => {
  const { lang, setLang } = useLang();
  const nav = NAV[lang];
  const [openGroup, setOpenGroup] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileGroup, setMobileGroup] = useState(null);
  const location = useLocation();

  useEffect(() => {
    setOpenGroup(null);
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className="fixed inset-x-0 top-0 z-50 border-b border-[#E4DDD0] bg-[#FBF8F3]/85 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="header-logo-button" className="transition-opacity duration-300 hover:opacity-80">
          <LogoFull light={false} />
        </Link>

        <nav className="hidden items-center gap-5 xl:flex" data-testid="header-nav">
          {nav.groups.map((group) => (
            <div
              key={group.key}
              className="relative"
              onMouseEnter={() => setOpenGroup(group.key)}
              onMouseLeave={() => setOpenGroup(null)}
            >
              <button
                data-testid={`nav-group-${group.key}`}
                className={`flex items-center gap-1.5 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-300 ${
                  openGroup === group.key ? "text-[#B36C45]" : "text-[#5C6B64] hover:text-[#0A1F18]"
                }`}
              >
                {group.label}
                <ChevronDown
                  className={`h-3 w-3 transition-transform duration-300 ${openGroup === group.key ? "rotate-180" : ""}`}
                />
              </button>
              <AnimatePresence>
                {openGroup === group.key && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute left-0 top-full w-64 border border-[#E4DDD0] bg-white/95 py-2 shadow-xl shadow-[#0A1F18]/5 backdrop-blur-xl"
                  >
                    {group.items.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        data-testid={`nav-${group.key}-${item.to.split("/").pop()}`}
                        className="block px-5 py-2.5 text-sm text-[#5C6B64] transition-colors duration-200 hover:bg-[#C87D55]/10 hover:text-[#0A1F18]"
                      >
                        {item.label}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
          {nav.links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.to.slice(1)}`}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#5C6B64] transition-colors duration-300 hover:text-[#0A1F18]"
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="flex items-center border border-[#E4DDD0] font-mono text-[11px] tracking-[0.2em]" data-testid="lang-switcher">
            {["fr", "en", "de"].map((l) => (
              <button
                key={l}
                data-testid={`lang-toggle-${l}`}
                onClick={() => setLang(l)}
                className={`px-3 py-1.5 uppercase transition-colors duration-300 ${
                  lang === l ? "bg-[#C87D55] text-[#FBF8F3]" : "text-[#5C6B64] hover:text-[#0A1F18]"
                }`}
              >
                {l}
              </button>
            ))}
          </div>

          <Link
            to="/contact"
            data-testid="header-cta-button"
            className="group hidden items-center gap-2 bg-[#0A1F18] px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-[#FBF8F3] transition-colors duration-300 hover:bg-[#C87D55] sm:flex"
          >
            {nav.cta}
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>

          <Link
            to="/admin"
            data-testid="header-admin-link"
            title="Accès admin"
            aria-label="Accès admin"
            className="hidden items-center justify-center border border-[#E4DDD0] p-2.5 text-[#5C6B64] transition-colors duration-300 hover:border-[#C87D55] hover:text-[#B36C45] sm:flex"
          >
            <Lock className="h-3.5 w-3.5" />
          </Link>

          <button
            data-testid="mobile-menu-button"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="text-[#0A1F18] xl:hidden"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="max-h-[70vh] overflow-y-auto border-t border-[#E4DDD0] bg-[#FBF8F3]/95 backdrop-blur-xl xl:hidden"
          >
            <div className="px-6 py-4">
              {nav.groups.map((group) => (
                <div key={group.key} className="border-b border-[#0A1F18]/5">
                  <button
                    data-testid={`mobile-nav-group-${group.key}`}
                    onClick={() => setMobileGroup(mobileGroup === group.key ? null : group.key)}
                    className="flex w-full items-center justify-between py-4 text-left font-display text-lg font-medium text-[#0A1F18]"
                  >
                    {group.label}
                    <ChevronDown
                      className={`h-4 w-4 text-[#C87D55] transition-transform duration-300 ${
                        mobileGroup === group.key ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {mobileGroup === group.key && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="pb-4 pl-3">
                          {group.items.map((item) => (
                            <Link
                              key={item.to}
                              to={item.to}
                              data-testid={`mobile-nav-${item.to.slice(1).replaceAll("/", "-")}`}
                              className="block py-2 text-sm text-[#5C6B64] transition-colors hover:text-[#B36C45]"
                            >
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
              {[...nav.links, { to: "/contact", label: nav.cta }, { to: "/admin", label: "Accès admin" }].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`mobile-nav-${link.to.slice(1)}`}
                  className="block border-b border-[#0A1F18]/5 py-4 font-display text-lg font-medium text-[#0A1F18] last:border-0"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
